import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ComponentType,
  type ReactNode,
} from 'react';
import { useParams, useNavigate } from 'react-router';
import { isAxiosError } from 'axios';
import {
  Stack,
  Alert,
  Card,
  Text,
  Group,
  Button,
  Skeleton,
  Select,
  Switch,
  List,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { IconAlertCircle, IconDeviceFloppy, IconCheck, IconArrowBackUp } from '@tabler/icons-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { StickyActionBar } from '../../components/ui/StickyActionBar';
import { TabNavProvider, TabPrevButton, TabNextButton } from '../../components/reports/TabNavContext';
import { useAuth } from '../../hooks/useAuth';
import { validateReport } from '../../services/reportsApi';
import { REPORT_TYPES } from '../../components/reports/reportTypes';
import { useReportForm } from '../../components/reports/useReportForm';
import { useReportCalc } from '../../components/reports/useReportCalc';
import { NotesBadge } from '../../components/reports/NotesBadge';
import { SubmitButton } from '../../components/reports/SubmitButton';
import { RevisionNotice } from '../../components/reports/RevisionNotice';
import { ReturnForRevisionModal } from '../../components/reports/ReturnForRevisionModal';
import { reportPermissions } from './reportPermissions';
import { actionBarState } from './actionBarState';
import { NO_REMOUNT } from '../../components/form/validatedFields';
import { RequiredMark } from '../../components/form/requiredMark';
import {
  createFieldTabRegistry,
  FieldTabRegistryContext,
} from '../../components/form/fieldTabRegistry';
import { FieldWarningsContext } from '../../components/form/fieldWarnings';
import { PreviousValuesContext } from '../../components/form/previousValues';
import { usePreviousValuesLoader } from '../../components/reports/usePreviousValuesLoader';
import { usePreviousValuesToggle } from '../../hooks/usePreviousValuesToggle';
// Namespace-Importe (Vorbild: `totalsModule` in `components/reports/formularD/DerivedTotal.tsx`)
// fuer die beiden reinen Funktionen -- haelt ihre Bezeichner je auf genau EINE Fundstelle im
// Modul begrenzt (den Aufruf selbst), statt sie zusaetzlich in einer Import-Zeile zu wiederholen.
import * as formStores from '../../components/reports/formStores';
import * as previousValuesHint from '../../components/reports/previousValuesHint';
import { ReportStatusBadge } from '../../components/reports/ReportStatusBadge';
import { tabConfigFor } from '../../components/reports/changedFieldGroups';
import { ReportNotes } from '../../components/reports/ReportNotes';
import {
  ReportStatus,
  ReportStatusLabels,
  ReportTypeIdentifier,
  ReportTypeIdentifierLabels,
  type FormularAB,
  type FormularC,
  type FormularD,
  type EnvironmentalData,
  type AccidentData,
  type AccidentCalculations,
  type EnvironmentalCalculations,
  type ReportFilters,
} from '../../types/reports';
import { getApiErrorMessage, getApiErrorMessages } from '../../utils/errorHandling';
import { formatPeriod } from '../../utils/reportPeriod';
import { statusPalette } from '../../theme/colors';
import { mapViolations, type Violation } from '../../utils/mapViolations';
import { buildReportPayload } from '../../components/reports/reportPayload';
import {
  buildAccidentInitialCalc,
  buildEnvironmentalInitialCalc,
} from '../../components/reports/initialCalc';

type ReportData = FormularAB | FormularC | FormularD | EnvironmentalData | AccidentData;

// REPORT_TYPES buendelt fuenf strukturell unterschiedliche Deskriptoren (je ein anderer
// FormData-Typ). TypeScript kann "reportType indexiert genau seinen eigenen Deskriptor"
// nicht ausdruecken, ohne die ganze Komponente generisch ueber den Typ zu parametrisieren
// (Aufwand ohne Mehrwert fuer eine Laufzeit-Registry). Die Randbedingungen (api/hydrate/
// Form gehoeren IMMER zusammen, weil sie aus demselben Registry-Eintrag stammen)
// sind Claude's Discretion laut 18-CONTEXT.md; ein einziger, hier zentralisierter Cast
// deckt alle polymorphen Aufrufstellen unten ab, statt sie einzeln zu verstreuen.
interface AnyReportTypeDescriptor {
  kind: string;
  api: {
    getOne: (id: number) => Promise<ReportData>;
    getAll: (filters?: ReportFilters) => Promise<ReportData[]>;
    update: (id: number, data: Record<string, unknown>) => Promise<ReportData>;
    submit: (id: number) => Promise<ReportData>;
    approve: (id: number) => Promise<ReportData>;
    returnForRevision: (id: number, reason: string) => Promise<ReportData>;
  };
  hydrate: (data: ReportData) => Record<string, unknown>;
  Form: ComponentType<Record<string, unknown>>;
  calcInputKeys?: readonly string[];
}

/**
 * Erklaertext unter der Seitenueberschrift. Der Standardtext gilt fuer Formulare, in denen
 * jedes Feld auszufuellen ist; Typen mit einer eigenen Pflichtfeldregel nennen stattdessen
 * die Markierung, weil die Aufforderung „bitte alle Felder ausfuellen" dort schlicht falsch
 * waere (Versand nach Produktkategorien schluesselt „davon"-Felder freiwillig auf; die
 * Zementartenstatistik hat keine Pflichtfelder und ist leer einreichbar).
 */
const DEFAULT_REPORT_DESCRIPTION =
  'Erfassen Sie die Daten für Ihr Werk. Bitte alle Felder ausfüllen und dort, wo nichts vorliegt, aktiv eine 0 eintragen.';

const REPORT_DESCRIPTIONS: Partial<Record<ReportTypeIdentifier, ReactNode>> = {
  [ReportTypeIdentifier.FORMULAR_C]: (
    <>
      Erfassen Sie die Daten für Ihr Werk. Pflichtfelder sind mit <RequiredMark /> markiert.
    </>
  ),
  [ReportTypeIdentifier.FORMULAR_D]: 'Erfassen Sie die Daten für Ihr Werk.',
};

export function ReportEditPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [report, setReport] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [preparingReturn, setPreparingReturn] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  // Die Fehlerbox haelt eine LISTE von Meldungen, nicht einen Fliesstext: eine 422-Antwort
  // traegt je Verstoss eine eigene Meldung, und verkettet ergaben sie einen unlesbaren
  // Bandwurm. Ein einzelner Eintrag wird ohne Aufzaehlungspunkt gerendert -- eine Liste mit
  // genau einem Punkt sieht nach einem abgeschnittenen Rest aus.
  const [error, setError] = useState<string[] | null>(null);
  const [plantsWithReports, setPlantsWithReports] = useState<Array<{ plantId: number; plantName: string; reportId: number; status: ReportStatus }>>([]);
  const [loadingPlants, setLoadingPlants] = useState(false);

  const reportType = type as ReportTypeIdentifier;
  const reportId = parseInt(id ?? '0', 10);

  // Rechte je Rolle x Meldungsart x Status (D-01/D-02/D-08): Spiegel der Backend-Matrix, reine
  // Komfortebene -- durchgesetzt wird im Backend (Voter, Prozessoren).
  const permissions = report ? reportPermissions(user, reportType, report.status) : null;
  const isOperationInProgress = saving || submitting || approving || preparingReturn;

  const { kind, api, hydrate, Form, calcInputKeys } =
    REPORT_TYPES[reportType] as unknown as AnyReportTypeDescriptor;

  // Seed fuer die Live-Vorschau (D-10): Unfall liest die persistierten Kennzahlen des
  // geladenen Reports (wie frueher initialCalculations in renderForm), Umwelt den
  // persistierten waterConsumption-Wert (analog EnvironmentalDataForm.tsx:77-79 vorher).
  // Alle anderen Typen bekommen keinen Seed -- useReportCalc macht dann keinen Netzaufruf,
  // solange calcInputKeys leer bleibt (formular_d) bzw. bis der Nutzer tatsaechlich tippt.
  // Der Unfall-Zweig ruft die ausgelagerte, reine Funktion buildAccidentInitialCalc auf
  // (initialCalc.ts) statt ein Objektliteral inline aufzubauen -- C-04/C-05. Der Umwelt-Zweig
  // spiegelt das seit Plan 30-05 mit buildEnvironmentalInitialCalc: Wasserverbrauch und die drei
  // abgeleiteten Brennstoffzeilen-Listen kommen gemeinsam aus einer reinen, getesteten Funktion.
  let initialCalc: AccidentCalculations | EnvironmentalCalculations | null = null;
  if (report && reportType === ReportTypeIdentifier.ACCIDENT) {
    initialCalc = buildAccidentInitialCalc(report as AccidentData);
  } else if (report && reportType === ReportTypeIdentifier.ENVIRONMENTAL) {
    initialCalc = buildEnvironmentalInitialCalc(report as EnvironmentalData);
  }

  const { calc, onValuesChange: calcOnValuesChange } = useReportCalc(
    kind,
    calcInputKeys ?? [],
    initialCalc,
    reportId || undefined
  );

  const {
    form,
    runValidation,
    enableSubmitValidation,
    disableSubmitValidation,
    applyWarnings,
    revalidateNow,
    valuesVersionStore,
    warningsStore,
  } = useReportForm(kind, {
    onValuesChange: calcOnValuesChange,
    disabled: !permissions?.canEdit || isOperationInProgress,
    reportId,
  });

  const actionBar = actionBarState(permissions);

  // Feld-zu-Reiter-Zuordnung der aktuell bearbeiteten Meldung (Plan 20-16). Sie wird von den
  // geteilten Feldkomponenten beim Mounten selbst gefuellt -- diese Seite haelt nur den
  // Behaelter und stellt ihn per Kontext bereit. Stabile Identitaet ueber useRef, damit die
  // Registry nicht bei jedem Render neu entsteht und ihr Wissen verliert.
  const fieldTabRegistry = useRef(createFieldTabRegistry()).current;

  // Vorwert-Store (D-17): dieselbe useRef(...).current-Instanziierung wie fieldTabRegistry,
  // stirbt beim Werkswechsel automatisch mit dem Route-Key `type/id` (App.tsx:114), ohne
  // Aufraeumcode.
  const previousValuesStore = useRef(formStores.createPreviousValuesStore()).current;

  // Sitzungsweiter Schalterzustand (D-09), EIN Schluessel fuer alle fuenf Formulartypen.
  const [showPreviousValues, setShowPreviousValues] = usePreviousValuesToggle();

  // Der Aufruf steht bei den uebrigen Hooks, VOR dem fruehen Return fuer `loading` --
  // Hooks duerfen nicht bedingt laufen. `plantId`/`reportingPeriod` sind `undefined`, solange
  // `report` noch nicht geladen ist; der Loader tut in diesem Fall nichts (siehe dort).
  const {
    state: previousValuesState,
    previousPeriod: previousValuesPeriod,
    totalsFailed: previousValuesTotalsFailed,
  } = usePreviousValuesLoader<ReportData>({
    enabled: showPreviousValues,
    kind,
    api,
    hydrate,
    plantId: report?.plant?.id,
    reportingPeriod: report?.reportingPeriod,
    store: previousValuesStore,
    // Phase 28 (D-15/D-16): liefert die aktuelle Ofenliste fuer die Positionszuordnung der
    // Drehofen-Vorwerte (`kiln.plantKiln.id`). Fuer die vier anderen Formulartypen ohne Wirkung.
    // Quelle ist der Formularzustand, nicht `report`: eine gerade angelegte Messung steht nur
    // dort. Der Zaehlerspeicher ist das zugehoerige Aenderungssignal -- bewusst der Store und
    // nicht sein Wert, damit der Zaehler hier nicht zu Parent-State wird (siehe dort).
    currentValues: form.getValues,
    valuesVersionStore,
  });

  // Transform plants data for Select component
  const plantSelectData = useMemo(() => {
    return plantsWithReports.map(p => ({
      value: String(p.reportId),
      label: `${p.plantName} (${ReportStatusLabels[p.status]})`,
    }));
  }, [plantsWithReports]);

  const handlePlantChange = (value: string | null) => {
    if (value && reportType) {
      navigate(`/reports/${reportType}/${value}`);
    }
  };

  /**
   * Laedt die Meldung neu und setzt Werte UND Dirty-Baseline neu auf.
   *
   * `silent` unterdrueckt ausschliesslich das seitenweite `loading`-Flag -- alles andere
   * (GET, setReport, setInitialValues, setValues) laeuft unveraendert.
   *
   * Warum das noetig ist: `loading` fuehrt in dieser Komponente zu einem fruehen Return mit
   * Skeletons. Der gesamte Formular-Teilbaum wird dabei ausgehaengt und danach neu aufgebaut
   * -- und mit ihm der aktive Reiter, der als `useState` in `useTabNavigation` INNERHALB
   * dieses Teilbaums liegt und beim Neuaufbau wieder bei `tabValues[0]` beginnt. Nach dem
   * Speichern sprang die Seite deshalb auf den ersten Reiter zurueck und verlor die
   * Bildlaufposition. Beim ERSTEN Oeffnen einer Meldung ist der Ladeanzeiger dagegen richtig:
   * dort gibt es noch kein Formular, das stehen bleiben koennte.
   */
  const fetchReport = useCallback(async (options?: { silent?: boolean }) => {
    if (!reportId || !reportType) return;

    const silent = options?.silent ?? false;

    try {
      if (!silent) setLoading(true);
      setError(null);

      const data = await api.getOne(reportId);
      setReport(data);

      const values = hydrate(data);
      // D-03-Rezept (siehe Mantine-Formular-Doku zu Werten fuer async geladene Daten):
      // setInitialValues (Dirty-Baseline) + setValues (Werte) -- NICHT die andere,
      // einmalige Initialisierungsmethode, deren Einmal-Guard das Re-Seeding nach dem
      // Speichern stumm verschlucken wuerde. Gilt fuer ALLE fuenf Typen, damit der
      // Notizen-Badge (eigenes form-Abo) ueberall greift.
      //
      // Beide Aufrufe laufen auch im stillen Nachladen: setInitialValues ist der
      // Vergleichsstand, an dem das Formular "ungespeichert" erkennt. Bliebe er beim
      // stillen Weg aus, gaelte eine gerade gespeicherte Meldung dauerhaft als geaendert.
      form.setInitialValues(values);
      form.setValues(values);
    } catch (err) {
      console.error('Failed to fetch report:', err);
      setError(['Fehler beim Laden der Meldung']);
    } finally {
      if (!silent) setLoading(false);
    }
    // api/hydrate sind ueber reportType bereits abgedeckt (Registry-Lookup,
    // stabil solange reportType gleich bleibt). form bewusst nicht als Dependency:
    // die zugrunde liegende uncontrolled-Werte-Ref ist stabil, ein bei jedem Render
    // neu erzeugtes form-Objekt wuerde diesen Callback sonst pro Render neu ausloesen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, reportType]);

  useEffect(() => {
    // Ohne `silent`: der Erstaufruf einer Meldung SOLL den Ladeanzeiger zeigen.
    fetchReport();
  }, [fetchReport]);

  // Fetch plants with reports of same type and period (verliert nur den Switch, D-02)
  useEffect(() => {
    const fetchPlantsWithReports = async () => {
      if (!report?.reportingPeriod || !reportType) {
        return;
      }

      try {
        setLoadingPlants(true);

        const reportsData = await api.getAll({ reportingPeriod: report.reportingPeriod });

        const plants = reportsData.map(r => ({
          plantId: r.plant.id,
          plantName: r.plant.name,
          reportId: r.id,
          status: r.status,
        }));

        setPlantsWithReports(plants);
      } catch (err) {
        console.error('Failed to fetch plants with reports:', err);
        setPlantsWithReports([]);
      } finally {
        setLoadingPlants(false);
      }
    };

    fetchPlantsWithReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, report?.reportingPeriod]);

  // Gemeinsamer try/catch + notifications.show + Loading-Flag-Rahmen (D-01): die drei
  // handle-Funktionen unterschieden sich vorher nur in Aktion, Erfolgstext und
  // Loading-Flag -- Zuschnitt ist Claude's Discretion laut 18-CONTEXT.md.
  async function runOperation(options: {
    setLoadingFlag: (value: boolean) => void;
    action: () => Promise<void>;
    successTitle: string;
    successMessage: string;
    errorFallback: string;
    onSuccess?: () => void | Promise<void>;
  }): Promise<void> {
    try {
      options.setLoadingFlag(true);
      setError(null);

      await options.action();

      notifications.show({
        title: options.successTitle,
        message: options.successMessage,
        color: 'green',
      });

      await options.onSuccess?.();
    } catch (err) {
      console.error(options.errorFallback, err);
      setError(getApiErrorMessages(err, options.errorFallback));
    } finally {
      options.setLoadingFlag(false);
    }
  }

  const SAVE_ERROR_FALLBACK = 'Fehler beim Speichern der Meldung';
  const SUBMIT_ERROR_FALLBACK = 'Fehler beim Einreichen der Meldung';

  // D-01: Ein 422 vom Server traegt dieselben strukturierten Violations wie die
  // Vorabvalidierung. Sie werden zusaetzlich zur Textmeldung auf die Felder gemappt,
  // damit beide Wege (Vorabvalidierung und Serverantwort) exakt dieselben Felder rot
  // markieren. Nicht-422-Fehler (Netz, 500) bringen keine Violations mit und lassen
  // die bestehenden Feldfehler unangetastet.
  //
  // `raiseSubmitMode` trennt die beiden Aufrufer: Der Einreichen-Pfad muss die
  // Validierungsstaerke anheben, sonst loescht der naechste Blur die frisch gesetzten
  // Pflichtfeld-Markierungen wieder (20-05). Der Speichern-Pfad darf das gerade NICHT --
  // er raeumt die Pflichtfeld-Markierungen ja absichtlich weg und wuerde sie sich mit
  // einem Wechsel in den submit-Modus beim naechsten Feldwechsel sofort zurueckholen.
  function applyServerViolations(err: unknown, options: { raiseSubmitMode: boolean }): void {
    if (!isAxiosError(err)) return;
    const violations = (err.response?.data as { violations?: Violation[] } | undefined)?.violations;
    if (Array.isArray(violations) && violations.length > 0) {
      if (options.raiseSubmitMode) enableSubmitValidation();
      form.setErrors(mapViolations(violations));
    }
  }

  // Speichern laeuft wie Einreichen (20-05) und Genehmigen (20-13) bewusst NICHT ueber
  // runOperation: beide Ausgaenge muessen die Feldmarkierungen anfassen, und runOperation
  // kennt in seinem catch-Zweig nur die Textmeldung. Ein optionaler Fehler-Callback waere
  // die kleinere Aenderung gewesen, haette aber eine Sonderregel in einen Rahmen gebaut,
  // den ausser dem Speichern nur noch der letzte Abschnitt des Einreichens benutzt --
  // dessen Fehlerbehandlung soll gerade NICHT mitwandern. Das Herausloesen folgt dem
  // etablierten Muster; runOperation bleibt fuer performSubmit unveraendert bestehen.
  //
  // Die Regel hinter beiden Ausgaengen: Nach einem Speicherversuch zeigen die
  // Feldmarkierungen ausschliesslich das, was den SPEICHERVORGANG betrifft -- nichts aus
  // einem frueheren Einreichen-Versuch. Fehlende Pflichtfelder blockieren das Speichern
  // nicht und verschwinden daher; fachlich falsche Werte (Default-Gruppe, z.B. die
  // K8-Regel) blockieren es und bleiben markiert.
  const handleSave = async () => {
    if (!reportId || !reportType) return;

    try {
      setSaving(true);
      setError(null);

      // D-01/D-04: handleSave liest den Snapshot ausschliesslich per form.getValues() --
      // die form ist fuer alle fuenf Typen die einzige schreibende Quelle.
      await api.update(reportId, buildReportPayload(kind, form.getValues()));

      notifications.show({
        title: 'Gespeichert',
        message: 'Die Meldung wurde erfolgreich gespeichert.',
        color: 'green',
      });

      // Erst neu laden, dann validieren: fetchReport befuellt die Form neu, und die
      // Nachvalidierung soll den frisch persistierten Stand pruefen, nicht den Stand von
      // vor dem Speichern.
      //
      // `silent`: kein seitenweiter Ladeanzeiger. Sonst wird der Formular-Teilbaum
      // ausgehaengt und neu aufgebaut, und der aktive Reiter faellt auf den ersten zurueck
      // (siehe Begruendung an fetchReport).
      await fetchReport({ silent: true });

      // Der submit-Modus muss VOR der Nachvalidierung fallen -- sonst pruefte diese
      // weiterhin gegen die Einreichen-Gruppe und schriebe genau die Pflichtfeld-
      // Markierungen zurueck, die sie raeumen soll.
      disableSubmitValidation();
      await revalidateNow();
    } catch (err) {
      console.error(SAVE_ERROR_FALLBACK, err);
      setError(getApiErrorMessages(err, SAVE_ERROR_FALLBACK));

      // Auch im Fehlerfall zuerst den submit-Modus abbauen: sonst zoege der naechste Blur
      // die Pflichtfeld-Markierungen wieder hoch und ueberdeckte genau die Felder, wegen
      // derer nicht gespeichert werden konnte. Der Bump der Sequenznummer darin verwirft
      // ausserdem eine noch fliegende Blur-Antwort, die die gleich gesetzten Markierungen
      // sonst ueberschreiben koennte.
      disableSubmitValidation();
      applyServerViolations(err, { raiseSubmitMode: false });
    } finally {
      setSaving(false);
    }
  };

  // Letzte Phase des Einreichens: der eigentliche Aufruf samt Erfolgsmeldung und
  // Navigation. Als eigene Funktion, weil sie aus zwei Wegen erreicht wird -- direkt
  // (keine Hinweise) und aus dem callbackbasierten onConfirm der Rueckfrage (D-11).
  const performSubmit = () => {
    if (!reportId) return;
    return runOperation({
      setLoadingFlag: setSubmitting,
      action: async () => {
        try {
          await api.submit(reportId);
        } catch (err) {
          applyServerViolations(err, { raiseSubmitMode: true });
          throw err; // runOperation zeigt daneben weiterhin die Textmeldung
        }
      },
      successTitle: 'Eingereicht',
      successMessage: 'Die Meldung wurde erfolgreich eingereicht.',
      errorFallback: SUBMIT_ERROR_FALLBACK,
      onSuccess: () => {
        navigate('/reports');
      },
    });
  };

  // Drei Phasen (K11/D-01, D-11): erst speichern und vorab mit mode=submit validieren,
  // bei offenen Hinweisen einmal rueckfragen, dann erst einreichen. Die Vorphase laeuft
  // bewusst AUSSERHALB von runOperation -- dieses zeigt nach jedem durchgelaufenen
  // `action` bedingungslos eine Erfolgsmeldung, und genau die darf beim Abbruch wegen
  // fehlender Pflichtfelder nicht erscheinen (das war der ausloesende Befund dieser Phase).
  const handleSubmit = async () => {
    if (!reportId || !reportType) return;

    let complete = false;
    let openWarnings = 0;
    try {
      setSubmitting(true);
      setError(null);

      await api.update(reportId, buildReportPayload(kind, form.getValues()));

      // Dieselbe Nutzlast wie der Speicheraufruf eine Zeile darueber -- buildReportPayload
      // traegt die Begruendung (reportPayload.ts).
      const { violations, warnings } = await validateReport(
        kind,
        buildReportPayload(kind, form.getValues()),
        'submit',
        reportId
      );

      // Beide Stufen derselben Antwort werden ausgewertet, nicht nur die blockierende:
      // dieselbe Antwort traegt Fehler UND Hinweise, und die Hinweise landeten bisher
      // nirgends -- der Warnungs-Store wurde ausschliesslich vom Blur-Weg gefuellt. Die
      // betroffenen Felder blieben deshalb ungefaerbt, bis ein spaeterer Feldwechsel
      // dieselben Hinweise ein zweites Mal holte. Der Aufruf steht VOR der Verzweigung,
      // weil er fuer beide Ausgaenge gilt: Abbruch wegen Fehlern und Weitergang zur
      // Rueckfrage. applyWarnings haelt dabei den Sequenzschutz aufrecht (siehe dort).
      applyWarnings(warnings);

      if (violations.length > 0) {
        // D-06: Felder markieren und hier abbrechen -- kein automatischer Tabwechsel,
        // kein Sprung zum Feld, keine zusammenfassende Fehlerliste. Der Nutzer bleibt
        // auf der Seite und auf seinem aktuellen Reiter; die Reiter-Kennzeichnung
        // (tabErrors.ts, gespeist aus der Render-Ableitung in fieldTabRegistry.ts)
        // zeigt ihm, wo die Felder liegen.
        // Ab jetzt prueft auch der Blur mit mode='submit' -- sonst wuerde der naechste
        // Fokuswechsel (schon ein Reiterklick genuegt) die Markierungen wieder loeschen.
        enableSubmitValidation();

        const fieldErrors = mapViolations(violations);
        form.setErrors(fieldErrors);

        const count = Object.keys(fieldErrors).length;
        // Die Marktformulare haben keine Reiter -- dort entfaellt der Satz zur Kennzeichnung.
        const hasTabs = tabConfigFor(reportType) !== null;
        notifications.show({
          title: 'Einreichen nicht möglich',
          message:
            count === 1
              ? `1 Feld wurde rot markiert.${hasTabs ? ' Der betroffene Reiter ist gekennzeichnet.' : ''}`
              : `${count} Felder wurden rot markiert.${hasTabs ? ' Die betroffenen Reiter sind gekennzeichnet.' : ''}`,
          color: 'yellow',
        });
      } else {
        complete = true;
        // Die Anzahl stammt aus GENAU DIESEM Validierungsaufruf, nicht aus dem
        // Warnungs-Store: der spiegelt den letzten Blur-Stand, die Rueckfrage muss
        // sich auf den gerade geprueften Formularstand beziehen.
        //
        // Das gilt UNVERAENDERT weiter, obwohl `applyWarnings` oben genau diese Hinweise
        // gerade in den Store geschrieben hat: der Store ist ein bewegliches Ziel. Zwischen
        // dem Schreiben und dem Oeffnen des Dialogs kann eine Blur-Antwort eintreffen und
        // ihn ersetzen, und `mapViolations` behaelt ausserdem nur den ERSTEN Hinweis je
        // Feld -- zwei Hinweise auf demselben Feld waeren im Store nur noch einer.
        // `warnings.length` aus dem Aufruf zu lesen ist deshalb kein Umweg, sondern die
        // einzige Zahl, die zum gerade geprueften Formularstand passt.
        openWarnings = warnings.length;
      }
    } catch (err) {
      console.error(SUBMIT_ERROR_FALLBACK, err);
      applyServerViolations(err, { raiseSubmitMode: true });
      setError(getApiErrorMessages(err, SUBMIT_ERROR_FALLBACK));
    } finally {
      // Der Ladezustand endet hier ueberall dort, wo der Submit-Aufruf NICHT unmittelbar
      // folgt: beim Abbruch wegen fehlender Pflichtfelder, bei einer Ausnahme und vor der
      // Rueckfrage. Waehrend der Dialog offen ist, laeuft keine Operation -- ein Spinner
      // waere dort eine Falschaussage, und beim Abbrechen muss der Button ohnehin wieder
      // bedienbar sein. Nur im durchlaufenden Fall uebernimmt runOperation das Flag
      // nahtlos, damit der Button zwischen den Phasen nicht aufblitzt.
      if (!complete || openWarnings > 0) setSubmitting(false);
    }

    if (!complete) return;

    if (openWarnings > 0) {
      // D-11: Hinweise blockieren nichts (D-09) -- sie verlangen nur eine bewusste
      // Bestaetigung. Abbrechen bedeutet: nichts tun. Kein Submit, keine Erfolgsmeldung,
      // keine Navigation; das Ladeflag ist im finally oben bereits zurueckgesetzt.
      modals.openConfirmModal({
        title: 'Hinweise zur Meldung',
        children: (
          <Text>
            {openWarnings === 1
              ? 'Es besteht 1 Hinweis — trotzdem einreichen?'
              : `Es bestehen ${openWarnings} Hinweise — trotzdem einreichen?`}
          </Text>
        ),
        labels: { confirm: 'Trotzdem einreichen', cancel: 'Abbrechen' },
        onConfirm: () => {
          void performSubmit();
        },
      });
      return;
    }

    return performSubmit();
  };

  const APPROVE_ERROR_FALLBACK = 'Fehler beim Genehmigen der Meldung';
  const RETURN_ERROR_FALLBACK = 'Fehler beim Zurückgeben der Meldung';

  // O-3: Genehmigen und Rueckgabe wirken auf den GESPEICHERTEN Stand. Wie beim Einreichen
  // speichert der Klick deshalb zuerst -- bedingungslos, ohne Erkennung ungespeicherter
  // Aenderungen. Scheitert das Speichern, markiert es die Felder wie handleSave und die
  // Aktion unterbleibt. Ein Pruefer-Speichern laesst Status und Aenderungsliste unberuehrt.
  const saveBeforeReviewAction = async (title: string, errorFallback: string): Promise<boolean> => {
    if (!reportId) return false;
    try {
      await api.update(reportId, buildReportPayload(kind, form.getValues()));
      return true;
    } catch (err) {
      console.error(errorFallback, err);
      disableSubmitValidation();
      applyServerViolations(err, { raiseSubmitMode: false });
      setError(getApiErrorMessages(err, errorFallback));
      notifications.show({ title, message: getApiErrorMessage(err, errorFallback), color: 'red' });
      return false;
    }
  };

  const handleOpenReturn = async () => {
    setPreparingReturn(true);
    setError(null);
    try {
      if (await saveBeforeReviewAction('Rückgabe nicht möglich', RETURN_ERROR_FALLBACK)) {
        setReturnModalOpen(true);
      }
    } finally {
      setPreparingReturn(false);
    }
  };

  // CR-01: Genehmigen laeuft bewusst NICHT ueber runOperation. Dieses zeigt nach jedem
  // durchgelaufenen `action` bedingungslos eine Erfolgsmeldung und navigiert weg -- solange
  // approve() ein wirkungsloses PATCH war, meldete die Oberflaeche damit einen Statuswechsel,
  // den es nie gab. handleSubmit wurde in 20-05 aus demselben Grund herausgezogen.
  //
  // Der Erfolgspfad haengt deshalb am TATSAECHLICHEN Ergebnis: nur wenn die Antwort den Status
  // `approved` traegt, erscheinen Erfolgsmeldung und Navigation. Diese Pruefung ist kein
  // Misstrauen gegen den frisch gebauten Endpunkt, sondern der Waechter gegen genau die
  // Rueckfall-Variante, die diese Phase ausgeloest hat: ein 2xx ohne Wirkung wuerde hier
  // auffallen statt gefeiert zu werden.
  // Genehmigen ist endgueltig (D-03) -- danach bearbeitet niemand mehr, auch der Pruefer nicht.
  // Deshalb eine Rueckfrage; Abbrechen tut nichts.
  const confirmApprove = () => {
    modals.openConfirmModal({
      title: 'Meldung genehmigen?',
      children: <Text>Danach kann die Meldung nicht mehr geändert werden.</Text>,
      labels: { confirm: 'Genehmigen', cancel: 'Abbrechen' },
      confirmProps: { color: 'green' },
      onConfirm: () => {
        void handleApprove();
      },
    });
  };

  const handleApprove = async () => {
    if (!reportId || !reportType) return;

    try {
      setApproving(true);
      setError(null);

      if (!(await saveBeforeReviewAction('Genehmigen nicht möglich', APPROVE_ERROR_FALLBACK))) return;

      const updated = await api.approve(reportId);

      const actualStatus = updated?.status;
      if (actualStatus !== ReportStatus.APPROVED) {
        const label = actualStatus ? ReportStatusLabels[actualStatus] : 'unbekannt';
        const message =
          'Der Server hat die Genehmigung bestätigt, die Meldung steht danach aber weiterhin auf '
          + `"${label}". Bitte laden Sie die Seite neu.`;
        setError([message]);
        notifications.show({
          title: 'Genehmigen nicht möglich',
          message,
          color: 'red',
        });
        return;
      }

      notifications.show({
        title: 'Genehmigt',
        message: 'Die Meldung wurde erfolgreich genehmigt.',
        color: 'green',
      });

      navigate('/reports');
    } catch (err) {
      console.error(APPROVE_ERROR_FALLBACK, err);
      // Die Meldung des Servers, nicht der generische Ersatztext: der Prozessor begruendet die
      // Ablehnung fachlich ("Nur eingereichte Meldungen sind genehmigungsfähig. Aktueller
      // Status: ..."), und getApiErrorMessage liest genau dieses `detail`-Feld.
      // Die Box bekommt die Einzelmeldungen (untereinander), die Notification die einzeilige
      // Fassung -- eine Notification ist eine schmale Blase, in der eine Aufzaehlung nicht
      // traegt. Beide Fassungen stammen aus derselben Auswertung.
      const message = getApiErrorMessage(err, APPROVE_ERROR_FALLBACK);
      setError(getApiErrorMessages(err, APPROVE_ERROR_FALLBACK));
      // Zusaetzlich als Notification: der Genehmigen-Knopf sitzt in der unteren Aktionsleiste,
      // der Fehler-Alert rendert am Seitenanfang und kann bei einem langen Formular vollstaendig
      // ausserhalb des Sichtfensters liegen. Ein nicht gesehener Fehler ist praktisch dasselbe
      // wie gar keine Rueckmeldung.
      notifications.show({
        title: 'Genehmigen nicht möglich',
        message,
        color: 'red',
      });
    } finally {
      setApproving(false);
    }
  };

  // Rueckgabe zur Ueberarbeitung (D-06): der Dialog ruft den POST und zeigt Feldfehler selbst;
  // hier nur der Erfolgspfad. Wie bei Genehmigen haengt er am tatsaechlichen Ergebnisstatus.
  const handleReturned = (updated: ReportData) => {
    if (updated?.status !== ReportStatus.REVISION_NEEDED) {
      const label = updated?.status ? ReportStatusLabels[updated.status] : 'unbekannt';
      const message =
        'Der Server hat die Rückgabe bestätigt, die Meldung steht danach aber weiterhin auf '
        + `"${label}". Bitte laden Sie die Seite neu.`;
      setReturnModalOpen(false);
      setError([message]);
      notifications.show({ title: 'Rückgabe nicht möglich', message, color: 'red' });
      return;
    }

    setReturnModalOpen(false);
    notifications.show({
      title: 'In Überarbeitung gegeben',
      message: 'Die Meldung wurde an das Werk zurückgegeben.',
      color: 'green',
    });
    navigate('/reports');
  };

  // Schaltet die einmalige Entdeckungsrunde der Reiterformulare, damit alle Beschriftungen fuer
  // die Aenderungsliste angemeldet sind (D-13). Nur Umwelt/Unfall deklarieren die Prop.
  const discoverAllFields = Boolean(
    permissions?.isReviewer
      && report?.status === ReportStatus.SUBMITTED
      && (report?.changedFieldsSinceRevision?.length ?? 0) > 0
  );

  const commonFormProps: Record<string, unknown> = {
    disabled: !permissions?.canEdit || isOperationInProgress,
    loading,
  };

  // Alle fuenf Formulare laufen ueber genau eine Prop-Grenze (D-04/D-11): keine
  // Zweig-Unterscheidung mehr, jedes Formular bekommt form/runValidation/valuesVersionStore
  // und liest ausschliesslich aus der geteilten form-Instanz.
  //
  // warningsStore folgt exakt dem Praezedenzfall valuesVersionStore (20-07): eine
  // Store-Instanz mit stabiler Identitaet als Prop. Er ist damit in allen fuenf
  // Formularen verfuegbar, ohne dass eine eingehende Warnung das Formular als Ganzes
  // neu rendert -- nur die abonnierenden Blattzellen reagieren.
  const formProps: Record<string, unknown> = {
    ...commonFormProps,
    form,
    runValidation,
    calc,
    valuesVersionStore,
    warningsStore,
    ...(reportType === ReportTypeIdentifier.ENVIRONMENTAL || reportType === ReportTypeIdentifier.ACCIDENT
      ? { discoverAllFields }
      : {}),
    ...(reportType === ReportTypeIdentifier.ACCIDENT ? { plant: report?.plant } : {}),
    // 27.1-07 (D-11): das Berichtsjahr fuer die Drehofen-Abschnittsueberschrift
    // "Jahresmittelwerte {Jahr}" gehoert zum Bericht, nicht zu seinen Werten -- deshalb als
    // Anzeige-Prop durch dieselbe Grenze wie `plant` beim Unfallformular, nicht ueber ein neues
    // Feld im Formularbaum.
    ...(reportType === ReportTypeIdentifier.ENVIRONMENTAL ? { reportingPeriod: report?.reportingPeriod } : {}),
  };

  if (loading) {
    return (
      <Stack>
        <Skeleton height={20} width={200} />
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Skeleton height={32} width={300} />
            <Group gap="md">
              <Skeleton height={20} width={150} />
              <Skeleton height={20} width={150} />
              <Skeleton height={24} width={80} />
            </Group>
          </Stack>
          <Skeleton height={36} width={100} />
        </Group>
        <Card padding="lg" radius="md" withBorder>
          <Skeleton height={400} />
        </Card>
      </Stack>
    );
  }

  if (!report) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Fehler" color="red">
        Meldung nicht gefunden
      </Alert>
    );
  }

  // D-06/D-07/D-21: der vierte Wert (Teilfehlschlag der Formular-D-Summen) kommt unveraendert
  // aus dem Loader und ist hier -- ohne den zweiten Aufruf aus Plan 24-08 -- immer `false`.
  const previousValuesHintMessage = previousValuesHint.previousValuesHintText(
    previousValuesState,
    previousValuesPeriod,
    report.configuration.interval,
    previousValuesTotalsFailed
  );

  return (
    <Stack>
      <PageHeader
        title={ReportTypeIdentifierLabels[reportType]}
        breadcrumb={{ label: 'Zurück zur Übersicht', to: '/reports' }}
        description={REPORT_DESCRIPTIONS[reportType] ?? DEFAULT_REPORT_DESCRIPTION}
        breadcrumbRight={
          <Stack gap={4} align="flex-end">
            <Switch
              label="Werte der vorherigen Meldung anzeigen"
              color="vdzOrange.6"
              checked={showPreviousValues}
              onChange={(event) => setShowPreviousValues(event.currentTarget.checked)}
            />
            {previousValuesHintMessage && (
              <Text size="xs" c="vdzGray.6">
                {previousValuesHintMessage}
              </Text>
            )}
          </Stack>
        }
        rightSection={
          <>
            <ReportStatusBadge status={report.status} />
            <Group gap={4} wrap="nowrap">
              <Text size="xs" c="vdzGray.6">Zeitraum:</Text>
              <Text size="sm" fw={700}>
                {formatPeriod(report.reportingPeriod, report.configuration.interval)}
              </Text>
            </Group>
            {plantSelectData.length > 1 ? (
              <Group gap={4} wrap="nowrap">
                <Text size="xs" c="vdzGray.6">Werk:</Text>
                <Select
                  data={plantSelectData}
                  value={String(reportId)}
                  onChange={handlePlantChange}
                  disabled={loadingPlants || isOperationInProgress}
                  size="xs"
                  w={200}
                />
              </Group>
            ) : (
              <Group gap={4} wrap="nowrap">
                <Text size="xs" c="vdzGray.6">Werk:</Text>
                <Text size="sm" fw={700}>
                  {report.plant?.name}
                </Text>
              </Group>
            )}
          </>
        }
      />

      {error && error.length > 0 && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Fehler"
          color="red"
          onClose={() => setError(null)}
          withCloseButton
        >
          {error.length === 1 ? (
            error[0]
          ) : (
            <List size="sm" spacing={4} data-testid="report-error-list">
              {error.map((message) => (
                <List.Item key={message}>{message}</List.Item>
              ))}
            </List>
          )}
        </Alert>
      )}

      {/* Die drei Kontexte, aus denen sich die geteilten Feldkomponenten selbst bedienen
          (Plan 20-16, Plan 24-05): der Hinweis zum eigenen Feld, die Anmeldung am eigenen
          Reiter und der Vorwert des Vorjahres. Alle drei gelten fuer ALLE fuenf Formulare --
          deshalb stehen sie hier und nicht in einem einzelnen Formular. warningsStore wird
          zusaetzlich weiterhin als Prop gereicht: die berechneten Zellen der Unfallmatrix
          sind keine Feldkomponenten und ordnen ihre Hinweise selbst zu. Der Vorwert-Store
          wird bewusst NICHT als Prop in formProps gereicht (D-15/D-17) -- jede Anzeigestelle
          holt sich ihren Vorwert selbst aus dem Kontext. */}
      <FieldTabRegistryContext.Provider value={fieldTabRegistry}>
      <FieldWarningsContext.Provider value={warningsStore}>
      <PreviousValuesContext.Provider value={previousValuesStore}>
      <TabNavProvider>
      <RevisionNotice
        reportType={reportType}
        status={report.status}
        reason={report.revisionReason}
        changedFields={report.changedFieldsSinceRevision}
        isReviewer={permissions?.isReviewer ?? false}
      />
      <Form {...formProps} />

      <StickyActionBar
        leftActions={<TabPrevButton disabled={isOperationInProgress} />}
        rightActions={
          <>
            <TabNextButton disabled={isOperationInProgress} />
            <NotesBadge form={form} onClick={() => setNotesOpen((open) => !open)} />
            {actionBar.showSave && (
              <Button
                variant="filled"
                leftSection={<IconDeviceFloppy size={16} />}
                onClick={handleSave}
                loading={saving}
                disabled={isOperationInProgress && !saving}
              >
                Speichern
              </Button>
            )}
            {actionBar.showSubmit && (
              <SubmitButton
                onClick={handleSubmit}
                loading={submitting}
                disabledExtra={isOperationInProgress && !submitting}
              />
            )}
            {actionBar.showReturn && (
              <Button
                variant="outline"
                leftSection={<IconArrowBackUp size={16} />}
                onClick={handleOpenReturn}
                loading={preparingReturn}
                disabled={isOperationInProgress && !preparingReturn}
                styles={{
                  root: {
                    borderColor: statusPalette.revision.border,
                    color: statusPalette.revision.fg,
                    fontWeight: 700,
                  },
                }}
              >
                In Überarbeitung geben
              </Button>
            )}
            {actionBar.showApprove && (
              <Button
                variant="filled"
                color="green"
                leftSection={<IconCheck size={16} />}
                onClick={confirmApprove}
                loading={approving}
                disabled={isOperationInProgress && !approving}
              >
                Genehmigen
              </Button>
            )}
          </>
        }
      />
      </TabNavProvider>
      </PreviousValuesContext.Provider>
      </FieldWarningsContext.Provider>
      </FieldTabRegistryContext.Provider>

      <ReturnForRevisionModal
        opened={returnModalOpen}
        onClose={() => setReturnModalOpen(false)}
        onSubmit={(reason) => api.returnForRevision(reportId, reason)}
        onReturned={handleReturned}
      />

      <ReportNotes
        opened={notesOpen}
        value={(form.getValues().notes as string | null | undefined) ?? null}
        onChange={(html) => {
          // form ist fuer ALLE fuenf Typen die einzige schreibende Quelle (D-04) --
          // NotesBadge liest ausschliesslich ueber form.watch('notes').
          form.setFieldValue('notes', html, NO_REMOUNT);
        }}
        disabled={!permissions?.canEdit || isOperationInProgress}
        onClose={() => setNotesOpen(false)}
      />
    </Stack>
  );
}
